import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { PriorityBadge } from "@/components/Badges";
import CommentThread from "@/components/CommentThread";
import { PROPOSAL_STATUS_META, rupiah, formatWIB } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ArchiveButton } from "@/components/ArchiveButton";
import {
  FileText, Plus, X, Send, CheckCircle2, RotateCcw, XCircle, GitBranch, Clock, History, ArrowRight, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);
const KANBAN = ["draft", "dalam_review", "revisi_diminta", "disetujui", "ditolak"];

function StatusPill({ status }) {
  const m = PROPOSAL_STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold", m.cls)}>{m.label}</span>;
}

function ApprovalChain({ steps, currentIdx }) {
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const color = s.status === "approved" ? "bg-emerald-500" : s.status === "conditional" ? "bg-teal-500"
          : s.status === "rejected" ? "bg-rose-500" : s.status === "revision" ? "bg-amber-500"
          : i === currentIdx ? "bg-indigo-600" : "bg-slate-300";
        return (
          <div key={s.id || i} className={cn("flex items-center gap-3 rounded-xl border p-2.5", i === currentIdx ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200")}>
            <div className={cn("flex h-7 w-7 items-center justify-center rounded-full text-white text-xs font-bold shrink-0", color)}>{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-700">{s.position_title} {s.kind === "parallel" && <span className="text-fuchsia-600 text-xs">(paralel)</span>}</p>
              <p className="text-xs text-slate-400">{s.role} · {s.approver_name || "(kosong)"}{s.note ? ` — "${s.note}"` : ""}</p>
            </div>
            <span className="text-xs font-medium text-slate-500 shrink-0">{s.status}</span>
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, children, sectionKey, pid }) {
  const [showC, setShowC] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold text-slate-500 uppercase">{title}</p>
        {sectionKey && <button onClick={() => setShowC((v) => !v)} className="text-xs text-indigo-600 hover:underline" data-testid={`section-comment-${sectionKey}`}>Komentar {showC ? "▲" : "▼"}</button>}
      </div>
      <div className="text-sm text-slate-700">{children}</div>
      {showC && sectionKey && <div className="mt-3 pt-3 border-t border-slate-100"><CommentThread entityType="proposal" entityId={pid} sectionKey={sectionKey} compact /></div>}
    </div>
  );
}

function ProposalDetail({ pid, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const [conditions, setConditions] = useState([""]);
  const [showVersions, setShowVersions] = useState(false);
  const [cmpA, setCmpA] = useState(0);
  const [cmpB, setCmpB] = useState(0);

  const load = () => api.get(`/proposals/${pid}`).then((r) => { setP(r.data); });
  useEffect(() => { if (open && pid) load(); }, [open, pid]);
  if (!p) return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent className="w-full sm:max-w-2xl" aria-describedby={undefined}><SheetHeader className="sr-only"><SheetTitle>Memuat detail pengajuan</SheetTitle></SheetHeader><div className="p-6 text-slate-400">Memuat...</div></SheetContent></Sheet>;

  const isRequester = p.requester_id === user?.id;
  const isAdmin = ["super_admin", "owner"].includes(user?.role);
  const cur = p.current_step;
  const isCurrentApprover = cur && (cur.approver_user_id === user?.id || isAdmin) && p.status === "dalam_review";

  const submit = async () => { try { await api.post(`/proposals/${pid}/submit`); toast.success("Pengajuan dikirim"); await load(); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };
  const withdraw = async () => { await api.post(`/proposals/${pid}/withdraw`); toast.success("Ditarik"); await load(); onChanged?.(); };
  const decide = async () => {
    if (!decision) return;
    if ((decision === "minta_revisi" || decision === "tolak") && !note.trim()) return toast.error("Catatan/alasan wajib diisi");
    try {
      await api.post(`/proposals/${pid}/decision`, { decision, note, conditions: decision === "setujui_bersyarat" ? conditions.filter((c) => c.trim()) : [] });
      toast.success("Keputusan tersimpan"); setDecision(null); setNote(""); setConditions([""]); await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto zoa-scroll p-0" data-testid="proposal-detail" aria-describedby={undefined}>
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b px-6 py-4">
          <SheetHeader><SheetTitle className="text-left">{p.title}</SheetTitle></SheetHeader>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StatusPill status={p.status} /><PriorityBadge priority={p.priority} />
            <span className="text-xs text-slate-500">Rp {Number(p.budget_total).toLocaleString("id-ID")}</span>
            <span className="text-xs text-slate-400">v{p.current_version}</span>
            {p.sla_age_hours != null && cur && <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium"><Clock className="h-3 w-3" />Di meja: {cur.position_title}, {p.sla_age_hours} jam</span>}
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-slate-400">Pengaju</p><p className="font-medium">{p.requester?.name}</p></div>
            <div><p className="text-xs text-slate-400">Sponsor</p><p className="font-medium">{p.sponsor?.name || "-"}</p></div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Rantai Persetujuan</p>
            <ApprovalChain steps={p.approval_steps || []} currentIdx={p.current_step_index} />
          </div>

          {p.background && <Section title="Latar Belakang">{p.background}</Section>}
          <Section title="Tujuan">{p.objective || "-"}</Section>
          <Section title="Target" sectionKey="target" pid={pid}>
            {(p.targets || []).map((t, i) => <div key={i}>• {t.name}: {t.unit === "rupiah" ? rupiah(t.value) : `${t.value} ${t.unit}`}</div>)}
          </Section>
          {p.strategy_approach && <Section title="Strategi / Pendekatan">{p.strategy_approach}</Section>}
          <Section title="Rencana Eksekusi">
            {(p.execution_plan || []).map((e, i) => <div key={i}>• {e.title} <span className="text-xs text-slate-400">(bobot {e.weight}, {e.estimated_hours}j, {formatWIB(e.deadline, false)})</span></div>)}
          </Section>
          <Section title="Timeline & Milestone" sectionKey="timeline" pid={pid}>
            {(p.timeline || []).map((t, i) => <div key={i}>• {t.milestone} — {formatWIB(t.date, false)}</div>)}
          </Section>
          <Section title="Anggaran" sectionKey="anggaran" pid={pid}>
            {(p.budget_items || []).map((b, i) => <div key={i} className="flex justify-between"><span>{b.item}</span><span>{rupiah(b.amount)}</span></div>)}
            <div className="flex justify-between font-bold border-t border-slate-100 mt-1 pt-1"><span>Total</span><span>{rupiah(p.budget_total)}</span></div>
          </Section>
          <Section title="Risiko & Mitigasi" sectionKey="risiko" pid={pid}>
            {(p.risks || []).map((r, i) => <div key={i}>• {r.risk} <span className="text-xs text-rose-500">({r.impact})</span> → {r.mitigation}</div>)}
          </Section>
          {p.success_indicators && <Section title="Indikator Keberhasilan">{p.success_indicators}</Section>}

          {p.conditions?.length > 0 && (
            <div className="rounded-xl bg-teal-50 border border-teal-200 p-3">
              <p className="text-xs font-semibold text-teal-700 uppercase mb-1">Syarat (jadi checklist wajib)</p>
              {p.conditions.map((c) => <div key={c.id} className="text-sm text-teal-800">• {c.text}</div>)}
            </div>
          )}

          {p.created_entities?.strategy_id && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Entitas Hasil Persetujuan Dibuat</p>
              <p className="text-xs text-emerald-700 mt-0.5">Strategi + {p.created_entities.task_ids?.length || 0} tugas + {p.created_entities.target_ids?.length || 0} target terbentuk otomatis (gerbang eksekusi).</p>
            </div>
          )}

          {/* Versions */}
          <div>
            <button onClick={() => setShowVersions((v) => !v)} className="text-sm font-semibold text-indigo-600 inline-flex items-center gap-1" data-testid="toggle-versions"><History className="h-4 w-4" />Riwayat Versi ({p.versions?.length || 0})</button>
            {showVersions && p.versions?.length > 0 && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2 items-center text-xs">
                  <span>Bandingkan</span>
                  <select value={cmpA} onChange={(e) => setCmpA(Number(e.target.value))} className="border rounded px-1 py-0.5">{p.versions.map((v, i) => <option key={i} value={i}>v{v.version}</option>)}</select>
                  <ArrowRight className="h-3 w-3" />
                  <select value={cmpB} onChange={(e) => setCmpB(Number(e.target.value))} className="border rounded px-1 py-0.5">{p.versions.map((v, i) => <option key={i} value={i}>v{v.version}</option>)}</select>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[cmpA, cmpB].map((idx, k) => (
                    <div key={k} className="rounded-lg border border-slate-200 p-2 bg-slate-50">
                      <p className="font-bold mb-1">v{p.versions[idx]?.version}</p>
                      <pre className="whitespace-pre-wrap text-[10px]">{JSON.stringify(p.versions[idx]?.data, null, 1)}</pre>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Actions */}
          <div className="space-y-2">
            {isRequester && ["draft", "revisi_diminta"].includes(p.status) && (
              <div className="flex gap-2">
                <Button size="sm" onClick={submit} data-testid="proposal-submit-btn" className="bg-indigo-600 hover:bg-indigo-700"><Send className="h-4 w-4 mr-1" />Ajukan</Button>
              </div>
            )}
            {isRequester && p.status === "dalam_review" && (
              <Button size="sm" variant="outline" onClick={withdraw} className="text-slate-600"><XCircle className="h-4 w-4 mr-1" />Tarik Pengajuan</Button>
            )}
            {isCurrentApprover && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
                <p className="text-xs font-semibold text-indigo-700 uppercase">Keputusan Anda</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant={decision === "setujui" ? "default" : "outline"} onClick={() => setDecision("setujui")} data-testid="decide-setujui" className={decision === "setujui" ? "bg-emerald-600" : ""}><CheckCircle2 className="h-4 w-4 mr-1" />Setujui</Button>
                  <Button size="sm" variant={decision === "setujui_bersyarat" ? "default" : "outline"} onClick={() => setDecision("setujui_bersyarat")} data-testid="decide-bersyarat" className={decision === "setujui_bersyarat" ? "bg-teal-600" : ""}>Setujui Bersyarat</Button>
                  <Button size="sm" variant={decision === "minta_revisi" ? "default" : "outline"} onClick={() => setDecision("minta_revisi")} data-testid="decide-revisi" className={decision === "minta_revisi" ? "bg-amber-600" : ""}><RotateCcw className="h-4 w-4 mr-1" />Minta Revisi</Button>
                  <Button size="sm" variant={decision === "tolak" ? "default" : "outline"} onClick={() => setDecision("tolak")} data-testid="decide-tolak" className={decision === "tolak" ? "bg-rose-600" : ""}><XCircle className="h-4 w-4 mr-1" />Tolak</Button>
                </div>
                {decision === "setujui_bersyarat" && (
                  <div>
                    <Label className="text-xs">Syarat (jadi checklist wajib)</Label>
                    {conditions.map((c, i) => (
                      <div key={i} className="flex gap-2 mb-1"><Input value={c} onChange={(e) => setConditions((a) => a.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Syarat ${i + 1}`} data-testid={`condition-${i}`} />{conditions.length > 1 && <Button variant="ghost" size="icon" onClick={() => setConditions((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}</div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setConditions((a) => [...a, ""])}><Plus className="h-4 w-4 mr-1" />Syarat</Button>
                  </div>
                )}
                {decision && <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan / alasan" rows={2} data-testid="decision-note" />}
                {decision && <Button size="sm" onClick={decide} data-testid="decision-submit-btn" className="bg-indigo-600 hover:bg-indigo-700">Kirim Keputusan</Button>}
              </div>
            )}
          </div>

          <Separator />
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Diskusi Umum</p>
            <CommentThread entityType="proposal" entityId={pid} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CreateProposalDialog({ open, onOpenChange, onDone }) {
  const [positions, setPositions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({ title: "", type: "rencana_kerja", division_id: "", priority: "normal", background: "", objective: "", strategy_approach: "", success_indicators: "" });
  const [targets, setTargets] = useState([{ name: "", unit: "rupiah", value: "" }]);
  const [plan, setPlan] = useState([{ title: "", assignee_position_id: "", weight: 100, estimated_hours: 8, deadline: "" }]);
  const [timeline, setTimeline] = useState([{ milestone: "", date: "" }]);
  const [budget, setBudget] = useState([{ item: "", amount: "" }]);

  useEffect(() => { if (open) { api.get("/org/positions").then((r) => setPositions(r.data)); api.get("/divisions").then((r) => setDivisions(r.data)); } }, [open]);
  const total = budget.reduce((s, b) => s + Number(b.amount || 0), 0);

  const create = async () => {
    if (!f.title.trim() || !f.objective.trim()) return toast.error("Judul & Tujuan wajib");
    const body = {
      ...f, budget_total: total,
      targets: targets.filter((t) => t.name).map((t) => ({ name: t.name, unit: t.unit, value: Number(t.value || 0) })),
      execution_plan: plan.filter((p) => p.title && p.assignee_position_id).map((p) => ({ ...p, weight: Number(p.weight), estimated_hours: Number(p.estimated_hours), deadline: toIso(p.deadline) })),
      timeline: timeline.filter((t) => t.milestone).map((t) => ({ milestone: t.milestone, date: toIso(t.date) })),
      budget_items: budget.filter((b) => b.item).map((b) => ({ item: b.item, amount: Number(b.amount || 0) })),
    };
    try { const r = await api.post("/proposals", body); toast.success("Draft pengajuan dibuat"); onDone?.(r.data.id); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Buat Pengajuan (Bottom-Up)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Judul</Label><Input value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} data-testid="proposal-title-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Jenis</Label><Select value={f.type} onValueChange={(v) => setF((x) => ({ ...x, type: v }))}><SelectTrigger data-testid="proposal-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="misi_baru">Misi/Program Baru</SelectItem><SelectItem value="strategi_baru">Strategi Baru</SelectItem><SelectItem value="rencana_kerja">Rencana Kerja</SelectItem><SelectItem value="perubahan">Pengajuan Perubahan</SelectItem></SelectContent></Select></div>
            <div><Label>Divisi</Label><Select value={f.division_id} onValueChange={(v) => setF((x) => ({ ...x, division_id: v }))}><SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger><SelectContent>{divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Prioritas</Label><Select value={f.priority} onValueChange={(v) => setF((x) => ({ ...x, priority: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="kritis">Kritis</SelectItem><SelectItem value="tinggi">Tinggi</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="rendah">Rendah</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label>Latar Belakang & Masalah</Label><Textarea value={f.background} onChange={(e) => setF((x) => ({ ...x, background: e.target.value }))} rows={2} /></div>
          <div><Label>Tujuan *</Label><Textarea value={f.objective} onChange={(e) => setF((x) => ({ ...x, objective: e.target.value }))} rows={2} data-testid="proposal-objective-input" /></div>
          <RowEditor label="Target Terukur *" rows={targets} setRows={setTargets} empty={{ name: "", unit: "rupiah", value: "" }} render={(r, up) => (
            <><Input placeholder="Nama target" value={r.name} onChange={(e) => up("name", e.target.value)} /><Select value={r.unit} onValueChange={(v) => up("unit", v)}><SelectTrigger className="w-28"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="rupiah">Rupiah</SelectItem><SelectItem value="unit">Unit</SelectItem><SelectItem value="persentase">Persen</SelectItem><SelectItem value="angka">Angka</SelectItem></SelectContent></Select><Input type="number" placeholder="Nilai" value={r.value} onChange={(e) => up("value", e.target.value)} className="w-28" /></>
          )} />
          <RowEditor label="Rencana Eksekusi (draft tugas) *" rows={plan} setRows={setPlan} empty={{ title: "", assignee_position_id: "", weight: 100, estimated_hours: 8, deadline: "" }} render={(r, up) => (
            <><Input placeholder="Judul tugas" value={r.title} onChange={(e) => up("title", e.target.value)} /><Select value={r.assignee_position_id} onValueChange={(v) => up("assignee_position_id", v)}><SelectTrigger className="w-40"><SelectValue placeholder="PIC" /></SelectTrigger><SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent></Select><Input type="datetime-local" value={r.deadline} onChange={(e) => up("deadline", e.target.value)} className="w-44" /></>
          )} />
          <RowEditor label="Timeline / Milestone *" rows={timeline} setRows={setTimeline} empty={{ milestone: "", date: "" }} render={(r, up) => (
            <><Input placeholder="Milestone" value={r.milestone} onChange={(e) => up("milestone", e.target.value)} /><Input type="datetime-local" value={r.date} onChange={(e) => up("date", e.target.value)} className="w-44" /></>
          )} />
          <RowEditor label={`Anggaran (Total: ${rupiah(total)})`} rows={budget} setRows={setBudget} empty={{ item: "", amount: "" }} render={(r, up) => (
            <><Input placeholder="Item" value={r.item} onChange={(e) => up("item", e.target.value)} /><Input type="number" placeholder="Rp" value={r.amount} onChange={(e) => up("amount", e.target.value)} className="w-40" /></>
          )} />
          <div><Label>Indikator Keberhasilan</Label><Input value={f.success_indicators} onChange={(e) => setF((x) => ({ ...x, success_indicators: e.target.value }))} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button onClick={create} data-testid="proposal-create-submit" className="bg-indigo-600 hover:bg-indigo-700">Simpan Draft</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowEditor({ label, rows, setRows, empty, render }) {
  const up = (i) => (k, v) => setRows((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 mb-1.5 items-center">{render(r, up(i))}{rows.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setRows((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}</div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setRows((a) => [...a, { ...empty }])}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
    </div>
  );
}

export default function Proposals() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => api.get("/proposals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { const o = params.get("open"); if (o) { setOpenId(o); setDrawer(true); } }, [params]);

  const openDetail = (id) => { setOpenId(id); setDrawer(true); };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><FileText className="h-7 w-7 text-indigo-500" />Pengajuan</h1>
          <p className="text-slate-500 text-sm">Bottom-up: ajukan rencana → approval berjenjang → gerbang eksekusi otomatis</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-proposal-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Buat Pengajuan</Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
        {KANBAN.map((col) => {
          const colItems = items.filter((i) => i.status === col || (col === "disetujui" && i.status === "disetujui_bersyarat"));
          return (
            <div key={col} className="rounded-2xl bg-slate-100/60 p-3" data-testid={`kanban-col-${col}`}>
              <p className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center justify-between">{PROPOSAL_STATUS_META[col]?.label}<span className="bg-white rounded-full px-2 py-0.5">{colItems.length}</span></p>
              <div className="space-y-2">
                {colItems.map((p) => (
                  <div key={p.id} onClick={() => openDetail(p.id)} data-testid={`proposal-card-${p.id}`}
                    className="cursor-pointer rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md transition-all">
                    <p className="text-sm font-semibold text-slate-800 leading-snug line-clamp-2">{p.title}</p>
                    <div className="flex items-center justify-between mt-2">
                      <PriorityBadge priority={p.priority} />
                      <span className="text-xs text-slate-400">Rp {(p.budget_total / 1_000_000).toFixed(1)}jt</span>
                    </div>
                    {p.current_step && p.status === "dalam_review" && <p className="text-xs text-slate-400 mt-1.5 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{p.current_step.position_title}</p>}
                    {(p.requester_id === user?.id || ["super_admin", "owner", "kepala_divisi", "atasan"].includes(user?.role)) && (
                      <div className="flex justify-end mt-1"><ArchiveButton url={`/proposals/${p.id}`} label="pengajuan ini" onDone={load} testid={`archive-proposal-${p.id}`} size="text" /></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <ProposalDetail pid={openId} open={drawer} onOpenChange={(o) => { setDrawer(o); if (!o) { params.delete("open"); setParams(params); } }} onChanged={load} />
      <CreateProposalDialog open={createOpen} onOpenChange={setCreateOpen} onDone={(id) => { load(); openDetail(id); }} />
    </div>
  );
}
